import type { JSX } from 'preact';
import { signal } from '@preact/signals';
import { beginLogin, logout } from '../auth.js';
import { t } from '../i18n.js';
import { Shell, TopBar } from './shell.js';
import { IconGallery, IconShared, IconSpace } from './icons.js';

/**
 * Les quatre états de la page, **avant même qu'il y ait un compte**.
 *
 * Ils sont distincts parce qu'ils appellent des issues distinctes : « pas de
 * session » mène à l'accueil, « le chargement a raté » mène à une déconnexion.
 * Renvoyer le second à l'accueil afficherait un bouton « se connecter » à
 * quelqu'un qui l'est déjà.
 */
export type Phase =
  | { readonly kind: 'landing'; readonly message?: string }
  | { readonly kind: 'busy'; readonly text: string }
  | { readonly kind: 'app' }
  | { readonly kind: 'fatal'; readonly message: string };

export const phase = signal<Phase>({ kind: 'busy', text: '' });

export function Root(): JSX.Element {
  const p = phase.value;
  switch (p.kind) {
    case 'landing':
      return <Landing message={p.message} />;
    case 'busy':
      return <Loading text={p.text} />;
    case 'app':
      return <Shell />;
    case 'fatal':
      return <Fatal message={p.message} />;
  }
}

/**
 * **L'accueil** — le seul écran que `clients.md` §9.1 décrivait comme « à
 * habiller, l'existant est brut ». Il l'était : un titre, une phrase grise, un
 * bouton.
 *
 * Ce qu'il montre maintenant n'est pas un argumentaire, et surtout pas une liste
 * de fonctionnalités : ce sont **les trois questions qui structurent
 * l'application**, dans l'ordre exact du rail qu'on trouvera derrière. Les lire
 * ici, c'est déjà connaître le plan — et c'est la seule promesse qu'on puisse
 * tenir avant la connexion, puisqu'il n'y a rien d'autre à montrer d'un espace
 * privé.
 *
 * Le titre n'a **pas** de police à lui : ce commentaire annonçait une serif que
 * `.hero-title` n'a jamais posée, et que E4 a retirée du produit entier
 * *(2026-08-17)*. Ce qui lui donne sa voix est sa taille et son interlettrage.
 */
function Landing({ message }: { readonly message?: string }): JSX.Element {
  // **Trois questions, deux destinations** — et ce n'est pas une incohérence.
  // Les deux premières sont les deux *axes* de *Mon espace* : ranger et
  // retrouver sont bien deux questions distinctes, elles ne sont simplement plus
  // deux endroits. L'accueil promet ce qu'on pourra demander, pas le plan du
  // rail.
  const questions = [
    { key: 'nav.space', name: 'nav.space', q: 'nav.space.q', icon: IconSpace },
    { key: 'space.all', name: 'space.axis.all', q: 'space.axis.allQ', icon: IconGallery },
    { key: 'nav.shared', name: 'nav.shared', q: 'nav.shared.q', icon: IconShared },
  ] as const;

  return (
    <div class="app-shell">
      <TopBar />
      <main class="hero">
        <h1 class="hero-title">
          {t('app.promiseHead')} <em>{t('app.promiseTail')}</em>
        </h1>
        <p class="hero-tagline">{t('app.tagline')}</p>
        {message ? (
          <p class="notice error" data-testid="error" role="alert">
            {message}
          </p>
        ) : null}
        <button
          type="button"
          class="btn primary large"
          data-testid="login"
          onClick={() => void beginLogin()}
        >
          {t('app.signIn')}
        </button>

        <ul class="hero-questions">
          {questions.map(({ key, name, q, icon: Icon }) => (
            <li key={key} class="hero-question">
              <span class="hero-question-icon" aria-hidden="true">
                <Icon size={22} />
              </span>
              <span class="hero-question-name">{t(name)}</span>
              <span class="hero-question-q">{t(q)}</span>
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
}

function Loading({ text }: { readonly text: string }): JSX.Element {
  return (
    <div class="app-shell">
      <TopBar />
      <main class="hero">
        <p class="notice busy" data-testid="busy" role="status">
          <span class="spinner" aria-hidden="true" />
          {text}
        </p>
      </main>
    </div>
  );
}

function Fatal({ message }: { readonly message: string }): JSX.Element {
  return (
    <div class="app-shell">
      <TopBar />
      <main class="hero">
        <h1 class="hero-title">{t('app.name')}</h1>
        <p class="notice error" data-testid="error" role="alert">
          {message}
        </p>
        <button type="button" class="btn quiet" data-testid="logout" onClick={() => void logout()}>
          {t('app.signOut')}
        </button>
      </main>
    </div>
  );
}
